import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useCallback } from "react";
import { Async } from "react-async";

import { convertToCsv, downloadCsvAsExcel } from "@/components/DataTable/sheet";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { formatAddress } from "@/helpers/address";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { useTranslation } from "@/translations";

type ExportAddressesModalProps = ModalBaseProps & {
  search: string;
  selectedAddressTypes: string[];
};

export function ExportAddressesModal({
  isOpened,
  onOpenChange,
  search,
  selectedAddressTypes,
}: ExportAddressesModalProps): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const postHog = usePostHog();
  const addressQueries = useAddressQueries();
  const { data: addresses, isLoading } = useQuery({
    ...addressQueries.list({
      search,
      addressTypes: selectedAddressTypes,
    }),
    enabled: isOpened,
  });

  const csv = useCallback(async () => {
    return {
      all: await convertToCsv(
        addresses?.items.flatMap((a) =>
          a.users.length === 0
            ? [
                {
                  building: a.building.name,
                  address: formatAddress(a),
                  locationCode: a.locationCode,
                  fullName: "",
                  email: "",
                  registered: "",
                },
              ]
            : a.users.map((u) => ({
                building: a.building.name,
                address: formatAddress(a),
                locationCode: a.locationCode,
                fullName: u.fullName,
                email: u.email,
                registered: u.registeredAt ? t("common.yes") : t("common.no"),
              })),
        ) || [],
      ),
      activated: await convertToCsv(
        addresses?.items.flatMap((a) =>
          a.users
            .filter((u) => u.registeredAt)
            .map((u) => ({
              building: a.building.name,
              address: formatAddress(a),
              locationCode: a.locationCode,
              fullName: u.fullName,
              email: u.email,
            })),
        ) || [],
      ),
      notActivated: await convertToCsv(
        addresses?.items.flatMap((a) =>
          a.users
            .filter((u) => !u.registeredAt)
            .map((u) => ({
              building: a.building.name,
              address: formatAddress(a),
              locationCode: a.locationCode,
              fullName: u.fullName,
              email: u.email,
            })),
        ) || [],
      ),
    };
  }, [addresses?.items, t]);

  return (
    <Modal.Root
      title={t("page.addresses.list.export-modal.title", {
        count: addresses?.total ?? 0,
      })}
      description={t("page.addresses.list.export-modal.description")}
      {...{
        isOpened,
        onOpenChange,
      }}
      isPending={isLoading}
    >
      <Modal.Body>
        <Box />
      </Modal.Body>
      <Async promiseFn={csv} onReject={console.error}>
        <Async.Resolved<{
          all: string;
          activated: string;
          notActivated: string;
        }>>
          {(x) => (
            <Modal.Actions>
              <Button
                variant="outline"
                onClick={() => {
                  void downloadCsvAsExcel(`activated-users-${sessionUser.project.name}`, x.activated);

                  postHog.capture("clicked_export_addresses", {
                    type: "activated_users",
                  });
                }}
              >
                {t("page.addresses.list.export-modal.btn.export-activated-users")}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  void downloadCsvAsExcel(`non-activated-users-${sessionUser.project.name}`, x.notActivated);

                  postHog.capture("clicked_export_addresses", {
                    type: "non_activated_users",
                  });
                }}
              >
                {t("page.addresses.list.export-modal.btn.export-non-activated-users")}
              </Button>
              <Button
                onClick={() => {
                  void downloadCsvAsExcel(`addresses-${sessionUser.project.name}`, x.all);

                  postHog.capture("clicked_export_addresses", {
                    type: "all",
                  });
                }}
              >
                {t("page.addresses.list.export-modal.btn.export-all-addresses")}
              </Button>
            </Modal.Actions>
          )}
        </Async.Resolved>
        <Async.Pending>
          <FullSizeLoader />
        </Async.Pending>
        <Async.Rejected>
          <p className="p-space-xl">{t("common.generic-error")}</p>
        </Async.Rejected>
      </Async>
    </Modal.Root>
  );
}
